/**
 * @fileoverview Applies EEA default-deny Consent Mode after client hydration.
 * @description Kept separate from consent-preferences to avoid an import cycle
 * through gtag → attribution → hydrate-region-cookie.
 *
 * @module lib/analytics/consent-eea-hydration-default
 */

import { invalidateConsentCache, CONSENT_STORAGE_KEY } from "./consent"
import {
  ANALYTICS_COOKIE_MAX_AGE_SEC,
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  CONSENT_ANALYTICS_MIRROR_COOKIE,
} from "./cookie-names"

const CONSENT_VERSION = 1

/**
 * Whether parsed localStorage consent matches the current schema and version.
 *
 * @param {unknown} obj - Parsed JSON value.
 * @returns {boolean} True when the value is valid stored consent.
 */
function isValidStoredConsent(obj: unknown): boolean {
  if (!obj || typeof obj !== "object") return false
  const candidate = obj as Record<string, unknown>
  return (
    typeof candidate.version === "number" &&
    candidate.version === CONSENT_VERSION &&
    typeof candidate.updatedAt === "string" &&
    typeof candidate.strictlyNecessary === "boolean" &&
    typeof candidate.analytics === "boolean" &&
    typeof candidate.functional === "boolean" &&
    typeof candidate.ads === "boolean" &&
    typeof candidate.doNotSellOrShare === "boolean" &&
    typeof candidate.gpcDetected === "boolean"
  )
}

/**
 * Whether the visitor has a persisted consent choice (validated localStorage or explicit cookie).
 *
 * @returns {boolean} True when a saved accept/reject choice exists.
 */
function hasPersistedConsentChoice(): boolean {
  if (typeof window === "undefined") return false
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (isValidStoredConsent(parsed)) return true
    }
  } catch {
    // ignore
  }
  try {
    const header = document.cookie
    return (
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`) ||
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

/**
 * Writes the analytics consent mirror cookie for edge middleware.
 *
 * @param {boolean} analyticsAllowed - Whether analytics consent is granted.
 * @returns {void}
 */
function setConsentAnalyticsMirrorCookie(analyticsAllowed: boolean): void {
  if (typeof document === "undefined") return
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_ANALYTICS_MIRROR_COOKIE}=${analyticsAllowed ? "1" : "0"}; Path=/; Max-Age=${ANALYTICS_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`
}

/**
 * Applies EEA/UK/CH default-deny Consent Mode when hydration confirms
 * `consentRequired` after the head bootstrap already granted globally
 * (missing `adsights_region_cr` on cacheable redirect hops).
 *
 * Mirrors the bootstrap's `__eea` branch: denies Consent Mode analytics and
 * seeds mirror=0 so future first-party storage writes are gated, WITHOUT writing
 * an explicit opt-out cookie. It deliberately does NOT purge attribution or
 * other first-party data already written under the rest-of-world default before
 * the region resolved — no law requires retroactively deleting already-stored
 * first-party data, and the site's posture is to retain it. Only future
 * collection is gated (by the region-aware storage gate).
 *
 * @returns {void}
 */
export function applyHydratedConsentRequiredDefault(): void {
  if (typeof window === "undefined") return
  if (hasPersistedConsentChoice()) return
  // NB: do NOT skip this for a GPC visitor. EEA/UK/CH consent-for-storage is a
  // separate legal basis from GPC — a confirmed consent-required region must get
  // the analytics default-deny (Consent Mode denied + mirror=0) until opt-in,
  // whether or not GPC is set. (Before GPC was decoupled from first-party
  // analytics, a global GPC deny made this redundant; it no longer is.)

  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      personalization_storage: "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    })
  }

  setConsentAnalyticsMirrorCookie(false)
  invalidateConsentCache()
}
